import {gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import * as analytics from 'analytics/Analytics';
import {ANALYTICS_MODULE} from 'analytics/AnalyticsTypes';
import {assert} from 'Asserts';
import {COMMON_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {TagSchema} from 'constants/RegistryConstants';
import {REGISTRY_TYPE} from 'constants/SourceConstants';
import CreateBioentityModal from 'folder-items/CreateBioentityModal';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  CreateChildEntityButtonQuery,
  CreateChildEntityButtonQueryVariables,
} from 'registry/child-entities/__generated/CreateChildEntityButtonQuery';
import CreateAndRegisterBioentityModal from 'registry/create-bioentity/CreateAndRegisterBioentityModal';
import * as CreateBioentityUtil from 'registry/create-bioentity/CreateBioentityUtil';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {getInitialTag} from 'schemas/tags/TagUtil';
import {disabled} from 'styles/Utilities';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import {TooltipWrapper} from 'ui-library/bs/Tooltip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import legacyRunModal from 'ui-library/legacyRunModal';

interface PlusButtonProps {
  onClick?: () => void;
  className?: string;
  tooltipTitle: string;
  dataTestAttrs?: DataTestAttrs;
}

export function PlusButton(props: PlusButtonProps): JSX.Element {
  return (
    <TooltipWrapper tooltipTitle={props.tooltipTitle}>
      <a onClick={props.onClick}>
        <BenchlingIcon
          icon="compose"
          className={classNames(props.className, 'u-mini-spacing-left')}
          dataTestAttrs={props.dataTestAttrs}
        />
      </a>
    </TooltipWrapper>
  );
}

export function DisabledPlusButton(props: {tooltipTitle: string}): JSX.Element {
  return (
    <PlusButton
      className={css(styles.disabled)}
      tooltipTitle={props.tooltipTitle}
      dataTestAttrs={{component: 'PlusButton', element: 'compose', key: 'disabled'}}
    />
  );
}

interface PresentationalCreateChildEntityButtonProps {
  parentEntityData: {
    id: string;
    editUrl: string;
    isArchived: boolean;
    isRegistered: boolean;
    name: string;
    tagSchemaId: string;
    folderId: string;
  };
  childEntitySchema: TagSchema;
  childEntityLinkField: SchemaField;
}

export function PresentationalCreateChildEntityButton(
  props: PresentationalCreateChildEntityButtonProps
): JSX.Element {
  if (props.parentEntityData.isArchived) {
    return <DisabledPlusButton tooltipTitle="Cannot create when entity is archived" />;
  }

  function runCreateModal(): void {
    analytics.trackEventWithModule(COMMON_ANALYTICS_ACTIONS.OPENED_MODAL, ANALYTICS_MODULE.REGISTRY, {
      modalType: 'create_child_entity',
      childEntitySchemaId: props.childEntitySchema.id,
      isRegistered: props.parentEntityData.isRegistered,
    });

    const tags = [
      {
        ...getInitialTag(props.childEntityLinkField),
        name: props.childEntityLinkField.name,
        targetFiles: [
          {
            id: props.parentEntityData.id,
            name: props.parentEntityData.name,
            url: props.parentEntityData.editUrl,
          },
        ],
        targetFileTagSchemaId: props.parentEntityData.tagSchemaId,
      },
    ];

    if (props.parentEntityData.isRegistered) {
      legacyRunModal(CreateAndRegisterBioentityModal, {
        createAndRegisterBioentityArgs: {
          ...CreateBioentityUtil.getDefaultCreateAndRegisterBioentityArgs(),
          tags,
          tagSchemaId: props.childEntitySchema.id,
        },
      });
    } else {
      CreateBioentityModal.open(CreateBioentityModal, {
        createBioentityArgs: {
          ...CreateBioentityUtil.getDefaultCreateBioentityArgs(),
          tagSchema: props.childEntitySchema,
          folderId: props.parentEntityData.folderId,
          tags,
        },
      });
    }
  }

  return (
    <PlusButton
      onClick={runCreateModal}
      tooltipTitle="Create entity"
      dataTestAttrs={{component: 'PlusButton', element: 'compose', key: 'createEntity'}}
    />
  );
}

function isRegistered(source: {sourceType: string}): boolean {
  return source.sourceType === REGISTRY_TYPE;
}

export const CREATE_CHILD_ENTITY_BUTTON_QUERY = gql`
  query CreateChildEntityButtonQuery($parentEntityId: StrictString!, $childSchemaId: StrictString!)
  @skipAudit
  @engComponent(name: REGISTRY) {
    parentEntity: bioentity(id: $parentEntityId) {
      ... on RegistryItemInterface {
        id
        archiveRecord {
          purpose
        }
        editUrl
        folder {
          id
          source {
            ...SourceSummaryGraphQL
          }
        }
        name
        tagSchema {
          id
          childEntityLinkConfig(childSchemaId: $childSchemaId) {
            id
            childSchemaLinkField: childSchemaLinkFieldJson
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
  ${getFragmentWrapper('SourceSummary').fragment}
`;

interface CreateChildEntityButtonProps {
  parentEntityId: string;
  childEntitySchema: TagSchema;
}

export default function CreateChildEntityButton(props: CreateChildEntityButtonProps): JSX.Element | null {
  return (
    <BenchlingQuery<CreateChildEntityButtonQuery, CreateChildEntityButtonQueryVariables>
      query={CREATE_CHILD_ENTITY_BUTTON_QUERY}
      renderLoading={() => <DisabledPlusButton tooltipTitle="Loading..." />}
      variables={{parentEntityId: props.parentEntityId, childSchemaId: props.childEntitySchema.id}}
    >
      {({parentEntity}) => {
        if (isInaccessible(parentEntity)) {
          assert(false, 'Parent entity should never be inaccessible');
        }

        assert(
          parentEntity.tagSchema != null && parentEntity.tagSchema.childEntityLinkConfig != null,
          'Expected parent entity to have schema and child entity link config'
        );

        return (
          <PresentationalCreateChildEntityButton
            parentEntityData={{
              id: parentEntity.id,
              editUrl: parentEntity.editUrl,
              isArchived: Boolean(parentEntity.archiveRecord),
              isRegistered: isRegistered(parentEntity.folder.source),
              name: parentEntity.name,
              tagSchemaId: parentEntity.tagSchema.id,
              folderId: parentEntity.folder.id,
            }}
            childEntitySchema={props.childEntitySchema}
            childEntityLinkField={parentEntity.tagSchema.childEntityLinkConfig.childSchemaLinkField}
          />
        );
      }}
    </BenchlingQuery>
  );
}

const styles = StyleSheet.create({
  disabled,
});
